/**
 * The single, prominent connection-status line at the top of the Printer Helper.
 * Collapses the various store/SDK signals into one clear cashier-facing state:
 * Connected · Connecting · Disconnected · No saved printer · Error.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MuiPowerIcon from '@mui/icons-material/Power';
import MuiPrintIcon from '@mui/icons-material/Print';
import MuiCancelIcon from '@mui/icons-material/Cancel';
import MuiWarningIcon from '@mui/icons-material/Warning';
import CircularProgress from '@mui/material/CircularProgress';
import MuiCheckCircleIcon from '@mui/icons-material/CheckCircle';

import Label from 'src/components/label';
import MuiIcon, { type MuiIconComponent } from 'src/components/mui-icon';

// ----------------------------------------------------------------------

export type BannerColor = 'success' | 'warning' | 'error' | 'info';

export interface PrinterStatusBannerProps {
  /** A printer has been saved/selected. */
  hasPrinter: boolean;
  /** The active printer reports a live connection. */
  connected: boolean;
  /** A connect / switch / refresh is in flight. */
  busy: boolean;
  /** The printer is connected and healthy (paper/cover/cutter OK). */
  ready?: boolean;
  /** Last error message, shown only when not connected. */
  errorMessage?: string | null;
}

export interface PrinterStatusState {
  label: string;
  detail: string;
  color: BannerColor;
  icon: MuiIconComponent | null;
}

/** Collapse the store/SDK signals into one cashier-facing status — shared by the
 *  full banner (status view) and the compact header chip (troubleshoot view). */
export function derivePrinterStatus({
  hasPrinter,
  connected,
  busy,
  ready,
  errorMessage,
}: PrinterStatusBannerProps): PrinterStatusState {
  if (busy) {
    return { label: 'Connecting…', detail: 'Talking to the printer', color: 'info', icon: null };
  }
  if (!hasPrinter) {
    return {
      label: 'No saved printer',
      detail: 'Connect a receipt printer to start printing',
      color: 'warning',
      icon: MuiPrintIcon,
    };
  }
  if (connected) {
    return ready
      ? {
          label: 'Connected',
          detail: 'Printer is ready',
          color: 'success',
          icon: MuiCheckCircleIcon,
        }
      : {
          label: 'Connected',
          detail: 'Needs attention — check the status below',
          color: 'warning',
          icon: MuiWarningIcon,
        };
  }
  if (errorMessage) {
    return {
      label: 'Error',
      detail: errorMessage,
      color: 'error',
      icon: MuiCancelIcon,
    };
  }
  return {
    label: 'Disconnected',
    detail: 'Printer is not responding',
    color: 'warning',
    icon: MuiPowerIcon,
  };
}

export default function PrinterStatusBanner({
  hasPrinter,
  connected,
  busy,
  ready,
  errorMessage,
}: PrinterStatusBannerProps) {
  const { label, detail, color, icon } = derivePrinterStatus({
    hasPrinter,
    connected,
    busy,
    ready,
    errorMessage,
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 2, py: 1.5, borderRadius: 1, bgcolor: 'background.neutral' }}
    >
      <Box sx={{ display: 'flex', color: `${color}.main` }}>
        {icon ? <MuiIcon icon={icon} width={28} /> : <CircularProgress size={24} color="info" />}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Label color={color} variant="soft">
          {label}
        </Label>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {detail}
        </Typography>
      </Box>
    </Stack>
  );
}
